import request from 'superagent';
import cloneDeep from 'lodash.clonedeep';
import { logSessionInfo } from '../services/logger';
import { aosTaskTypes, hasStructuredInstructionsForTestletType, isFormativeTestlet } from '../constants/constants';
import { getUserData } from './API';
import { COPY_STRINGS } from '../constants/translatedCopy';
import networkLogger from './networkLogger';
import { graphQLRequestIAM } from './graphQLService';

const AOSurl = process.env.AOS_BASE_URL;

// ======== MOCK AOS CONFIGURATION ========
// Replaces GraphQL getTestlet responses in **development only** (NODE_ENV !== production).
//
// URL: ?mockAOS=true&mockSuite=listeningComprehension&mockIndex=20
// Optional: ?mockLogTestlet=true — reserved for future use; currently a no-op.
// App calls applyStickyMockAosFromInitialParams(initialUrlParams) at startup — same cache as restartSession / ReaderManager
// (params survive after OAuth or client routing strips the query string). isMockAOSNow() = sticky OR live URL.
//   mockSuite: listeningComprehension | writtenCommunication | vocabulario | gpc | blending | blendingSpanish | spelling | formativeUxWalk
//   mockIndex: 0-based index into that suite's testlet array (optional; default 0). Counter advances each getTestlet.
//
// **Entry:** With ?mockAOS=true, App/ReaderManager route into getTestlet (mock data) from tutor too, using
// placeholder assignment ids when needed. Real AOS still uses your assessment assignment as before.
//
// Suites are lazy-loaded so they are not bundled unless mock AOS is used.
// Note: Keys are camelCase URL parameter names (e.g., ?mockSuite=writtenCommunication), matching testlet type names.
const MOCK_SUITE_LOADERS = {
  listeningComprehension: () => import('./aosMocks/listeningComprehensionSuite'),
  writtenCommunication: () => import('./aosMocks/writtenCommunicationSuite'),
  vocabulario: () => import('./aosMocks/vocabularioSuite'),
  gpc: () => import('./aosMocks/gpcSuite'),
  blending: () => import('./aosMocks/blendingSuite'),
  blendingSpanish: () => import('./aosMocks/blendingSpanishSuite'),
  spelling: () => import('./aosMocks/spellingSuite'),
  formativeUxWalk: () => import('./aosMocks/formativeUxWalkSuite'),
};
// ========================================

const _isDev = process.env.NODE_ENV !== 'production';
// Avoid parsing boot URL / sticky mock state when this module loads in production (SpeechStream/Speech import path).
const _bootSearch = _isDev && typeof window !== 'undefined' ? window.location.search : '';
const _mockAosParams = new URLSearchParams(_bootSearch);

/** Sticky mock flag — set from App.initialUrlParams (constructor) and/or first URL; survives query string loss. */
let _stickyMockAOS = _isDev && _mockAosParams.get('mockAOS') === 'true';

let _resolvedSuiteName = _mockAosParams.get('mockSuite') || 'listeningComprehension';
const _bootMockIndex = parseInt(_mockAosParams.get('mockIndex'), 10);
let _resolvedStartIndex = Number.isNaN(_bootMockIndex) ? 0 : _bootMockIndex;
let mockSuite = null;

// Module-scoped counter: survives HMR when only component files reload. Use ?mockIndex=n to realign.
let mockTestletIndex = _resolvedStartIndex;

/**
 * Call from App constructor with the same `initialUrlParams` object built from the first URLSearchParams
 * (keeps mock AOS in sync with restartSession / ReaderManager initialUrlParams pattern).
 */
export function applyStickyMockAosFromInitialParams(params = {}) {
  if (process.env.NODE_ENV === 'production') return;
  if (!params || typeof params !== 'object') return;

  if (params.mockAOS === 'false' || params.mockAOS === '0') {
    _stickyMockAOS = false;
    return;
  }

  if (params.mockAOS === 'true') {
    _stickyMockAOS = true;
  }

  if (params.mockSuite != null && params.mockSuite !== '') {
    const next = String(params.mockSuite);
    if (next !== _resolvedSuiteName) {
      mockSuite = null;
    }
    _resolvedSuiteName = next;
  }

  if (params.mockIndex != null && params.mockIndex !== '') {
    const n = parseInt(params.mockIndex, 10);
    if (!Number.isNaN(n)) {
      _resolvedStartIndex = n;
      mockTestletIndex = n;
    }
  }
}

/**
 * Dev only: clear sticky mock flag and cached suite so the next getTestlet uses real AOS unless the URL
 * still has mockAOS=true. Call when finishing an activity if the session was never opened with ?mockAOS=true
 * (avoids HMR / stray sticky state); full reload also resets module scope.
 */
export function clearDevStickyMockAosState() {
  if (process.env.NODE_ENV === 'production') return;
  _stickyMockAOS = false;
  mockSuite = null;
}

/** Dev only: sticky (from App.initialUrlParams) OR current window.location.search. */
export function isMockAOSNow() {
  if (process.env.NODE_ENV === 'production') return false;
  if (_stickyMockAOS) return true;
  try {
    return new URLSearchParams(window.location.search).get('mockAOS') === 'true';
  } catch (e) {
    return false;
  }
}

async function getMockSuite() {
  if (mockSuite) {
    return mockSuite;
  }
  let suiteLoader =
    MOCK_SUITE_LOADERS[_resolvedSuiteName];
  if (!suiteLoader) {
    console.warn(`[mock_aos] unknown mockSuite: '${_resolvedSuiteName}', falling back to listeningComprehension`);
    suiteLoader = MOCK_SUITE_LOADERS.listeningComprehension;
  }
  const suiteModule = await suiteLoader();
  const loaded = suiteModule?.default ?? suiteModule;
  if (!Array.isArray(loaded)) {
    logSessionInfo('mock_aos_suite_load_invalid', {
      suite: _resolvedSuiteName,
      moduleKeys: suiteModule && typeof suiteModule === 'object' ? Object.keys(suiteModule) : [],
    });
    mockSuite = [];
    return mockSuite;
  }
  mockSuite = loaded;
  return mockSuite;
}

/** Reset which mock testlet is served next (dev). Call when cursor walked past the end of the suite. */
export function resetMockAosTestletCursor() {
  if (process.env.NODE_ENV === 'production') return;
  mockTestletIndex = _resolvedStartIndex;
}

/**
 * Serialize mock getTestlet cursor updates. Concurrent callers (double grade tap, overlapping flows)
 * must not both pass `await getMockSuite()` and then read the same index — the second would see
 * index >= length and return assignmentComplete, which surfaces as "couldn't find your stories".
 */
let _mockTestletResponseChain = Promise.resolve();


async function getMockTestletResponse() {
  const previous = _mockTestletResponseChain;
  let releaseNext;
  _mockTestletResponseChain = new Promise((resolve) => {
    releaseNext = resolve;
  });
  await previous;
  try {
    const activeMockSuite = await getMockSuite();

    if (mockTestletIndex >= activeMockSuite.length) {
      return {
        getTestlet: {
          context: { assignmentComplete: true, error: null },
          testlet: null,
          activityId: null,
        },
      };
    }
    const response = cloneDeep(activeMockSuite[mockTestletIndex]);
    mockTestletIndex++;

    return response;
  } finally {
    releaseNext();
  }
}

// getTestlet — full selection (testlet/item instructions, MMC/SB fields, questions).
const GET_TESTLET = `
  query getTestlet(
    $activityId: ID,
    $assignmentId: ID!, 
    $studentId: ID!, 
    $districtId: Int!, 
    $schoolId: Int!,
    $gradeLevel: Int,
    $locale: Locale
  ) {
    getTestlet(
      activityId: $activityId,
      assignmentId: $assignmentId, 
      studentId: $studentId, 
      districtId: $districtId, 
      schoolId: $schoolId,
      gradeLevel: $gradeLevel,
    ) {
      context {
        maxGradeLevel
        levelingDelta
        doNotRelevel
        assignmentComplete \n` +
        `error {
          selectionError
          details
          source
        }
      }
      testlet(locale: $locale) {
        testletId
        type
        subType
        grade
        tags
        nonAudio
        referenceStoryPhrases
        instructions {
          repeatVariation
          repeatable
          maxRepeats
          text
          type
          verbalInstructions
          imageURL
          videoURL
          transition
          model
          options {
            imageUrl
            text
            key
          }
        }
        items {
          gridWidth
          itemId
          minPhraseTime
          operational
          phrase
          timeoutInMS
          writtenInstructions
          type
          imageURL
          videoURL
          version
          startingWord
          wordPartToDelete
          letterDisplay
          wordDisplay
          readOptionsAloud
          verticalOptions
          canViewReferenceStory
          mode
          backgroundImageUrl
          options {
            imageUrl
            text
            key
            audioText
            videoUrl
          }
          dropZones {
            kind
            key
            capacity
            text
            imageUrl
            audioText
          }
          instructions {
            repeatVariation
            repeatable
            maxRepeats
            postStimulusQuestion
            text
            type
            verbalInstructions
            imageURL
            videoURL
            transition
            model
          }
          questions { 
            correct
            feedbackMode
            distractors
            distractorFormat
            text
            type
            readQuestion
          }
        }
      }
      activityId
    }
  }`;

const START_ASSESSMENT = `
  mutation startAssessment(
    $studentId: ID!,
    $assignmentId: ID!,
    $gradeLevel: Int!,
    $districtId: Int!,
    $schoolId: Int!,
    $onCampus: Boolean,
    $extendAssessmentTime: Boolean,
    $locale: String
  ) {
    startAssessment(
      studentId: $studentId,
      assignmentId: $assignmentId,
      gradeLevel: $gradeLevel,
      districtId: $districtId,
      schoolId: $schoolId,
      onCampus: $onCampus,
      extendAssessmentTime: $extendAssessmentTime,
      locale: $locale
    ) {
      assignmentId
    }
  }`;

async function startAssessment({ studentId, assignmentId, grade, districtId, schoolId, onCampus = false, extendAssessmentTime = false, locale = null }) {
  const input = {
    studentId,
    assignmentId,
    gradeLevel: grade,
    districtId,
    schoolId,
    onCampus,
    extendAssessmentTime,
  };

  // Add locale if provided
  if (locale) {
    input.locale = locale;
  }

  const response = await graphqlRequest(START_ASSESSMENT, input);

  if (!response.startAssessment || !response.startAssessment.assignmentId) {
    throw new Error("Failed to start assessment.");      
  }
}

async function startAssessmentWithRetries({ studentId, assignmentId, grade, districtId, schoolId, onCampus = false, extendAssessmentTime = false, locale = null }, retryCount = 0) {
  if (isMockAOSNow()) {
    return;
  }

  try {
    await startAssessment({ studentId, assignmentId, grade, districtId, schoolId, onCampus, extendAssessmentTime, locale });
  } catch (error) {
    if (retryCount < 3) {
      logSessionInfo("aos_start_assessment_retry", {
        retryCount: retryCount + 1,
        assignmentId
      });

      return await startAssessmentWithRetries({ studentId, assignmentId, grade, districtId, schoolId, onCampus, extendAssessmentTime, locale }, retryCount + 1);
    } else {
      throw error;
    }
  }
}

/**
 * Normalize pipe "|" to "[split]" in testlet instructions. Phrase segments on [split].
 * Applies to structured instruction types only (LC, SB, vocabulary, GPC). We only normalize
 * these because they *expect* pipes as spoken clause boundaries. We don't want to accidentally
 * break other testlet types that may already have literal pipes in their content.
 * Policy: new testlet types will be expected to use [split] markers moving forward.
 * Normalize at ingest so first play and repeat replay stay aligned without changing mocks/backend.
 * Note: Mutates testlet in place. This is intentional — buildStoryFromTestlet is the single entry point
 * for testlet processing, and the object is not cached or reused elsewhere.
 */
function normalizePipeClausesToSplit(testlet) {
  if (!hasStructuredInstructionsForTestletType(testlet.type) && !isFormativeTestlet(testlet)) return;

  const norm = (instr) => {
    if (!instr || typeof instr !== 'object') return;
    if (typeof instr.verbalInstructions === 'string') {
      instr.verbalInstructions = instr.verbalInstructions.split('|').join('[split]');
    }
    if (typeof instr.repeatVariation === 'string') {
      instr.repeatVariation = instr.repeatVariation.split('|').join('[split]');
    }
  };
  norm(testlet.instructions);
  for (const item of testlet.items || []) {
    norm(item.instructions);
  }
}

export const buildStoryFromTestlet = (testlet, previousType, context) => {
  normalizePipeClausesToSplit(testlet);

  const isFormative = isFormativeTestlet(testlet);

  const phrases = testlet.items.map(item => item.phrase || "");
  const items = testlet.items;

  // Instructions now sit at the testlet level, and need to given on the first item of a new testlet type
  // Always give testlet instructions for RAN and listeningComprehension (each testlet has its own story/passage)
  if (previousType !== testlet.type || testlet.type === aosTaskTypes.ran || testlet.type === aosTaskTypes.listeningComprehension) {
    if (testlet.instructions) {
      if (!testlet.instructions.type && (!testlet.instructions.text || testlet.instructions.text === "")) {
        testlet.instructions.text = " ";
      }
      if (testlet.type === aosTaskTypes.beginningLetterSound) {
        // Store the first item's instructions separately to play after testlet instructions
        // This allows the gato example to play first, then visual changes, then first item instructions
        const firstItemInstructions = items[0].instructions;
        items[0].firstItemVerbalInstructions = firstItemInstructions?.verbalInstructions || `Por favor, diga el primer sonido de la ${items[0].startingWord || ''}`;
      }
      // listeningComprehension / writtenCommunication / vocabulary / graphemePhonemeConversion: keep items[0].instructions as the item
      // (triggers, repeatVariation, etc.). Put testlet-level FTUE verbal on introductions — same pattern as
      // tutor wordList (verbalIntroductions → then instructions). For vocabulary/GPC this ensures [highlight:repeat]
      // on item 0's per-item instructions fires after the item UI is visible, not during FTUE.
      let skipFirstItemInstructionOverwrite = false;
      if (hasStructuredInstructionsForTestletType(testlet.type) || isFormative) {
        const firstItemInstructions = items[0].instructions;
        const testletIntro = testlet.instructions;
        if (firstItemInstructions?.verbalInstructions && testletIntro?.verbalInstructions) {
          const t = testletIntro;
          items[0].introductions = {
            verbalIntroductions: {
              full: t.verbalInstructions,
              short: t.verbalInstructions,
            },
            textIntroductions: {
              full: t.text || ' ',
              short: t.text || ' ',
            },
            // Formative names the testlet for the activity and the item for the interaction,
            // so the task type it is served under (multipleChoice, spelling, wif) is not a
            // component. Phrase renders the introduction off this type, so the authored one
            // wins: a Multiple Choice suite is served under a task type no component matches
            // and cannot be told apart from the item type alone.
            type: t.type || (isFormative ? items[0].type : testlet.type),
            repeatable: t.repeatable,
            maxRepeats: t.maxRepeats,
            repeatVariation: t.repeatVariation,
            ...(t.imageURL != null && t.imageURL !== '' ? { imageURL: t.imageURL } : {}),
            ...(t.videoURL != null && t.videoURL !== '' ? { videoURL: t.videoURL } : {}),
          };
          skipFirstItemInstructionOverwrite = true;
        }
      }
      // Spanish blending (mmc items): testlet-level intro → introductions, keep per-item trigger instructions.
      // Scoped to mmc so English blendingSelection items are unaffected.
      if (!skipFirstItemInstructionOverwrite && items[0].type === 'mmc') {
        const firstItemInstructions = items[0].instructions;
        const testletIntro = testlet.instructions;
        if (firstItemInstructions?.verbalInstructions && testletIntro?.verbalInstructions) {
          const t = testletIntro;
          items[0].introductions = {
            verbalIntroductions: {
              full: t.verbalInstructions,
              short: t.verbalInstructions,
            },
            textIntroductions: {
              full: t.text || ' ',
              short: t.text || ' ',
            },
            type: t.type || testlet.type,
            repeatable: t.repeatable,
            repeatVariation: t.repeatVariation,
            ...(t.imageURL != null && t.imageURL !== '' ? { imageURL: t.imageURL } : {}),
          };
          skipFirstItemInstructionOverwrite = true;
        }
      }
      // When false: first item gets testlet.instructions. For LC/SB, skip is true only when both sides have
      // verbalInstructions (FTUE → introductions); otherwise legacy overwrite — first item should define its own
      // verbalInstructions when it must differ from testlet-level FTUE.
      if (!skipFirstItemInstructionOverwrite) {
        items[0].instructions = testlet.instructions;
      }
    }

    if (items[0].type === "blendingSelection") {
      // Only apply this to the first instance of blendingSelection on a set of testlets
      items[0].playComponentIntroductions = true;
    }

    if (testlet.type === aosTaskTypes.readingComprehension && getUserData().nonverbal) {
      // Requirement from product and services teams to hardcode the non-verbal instructions for reading comprehension
      items[0].instructions.verbalInstructions = COPY_STRINGS.reading_comprehension_intro_NON_VERBAL_ONE;
    }
  }
  
  for (let i = 1; i < items.length; i++) {
    if (items[i].type === "spelling" || testlet.type === "spelling" || testlet.type === aosTaskTypes.beginningLetterSound) {
      if (!items[i].instructions) {
        items[i].instructions = {};
      }

      if (!items[i].instructions.verbalInstructions) {
        if (testlet.type === aosTaskTypes.beginningLetterSound) {
          items[i].instructions.verbalInstructions = `Por favor, diga el primer sonido de la ${items[i].startingWord || ''}`;
          items[i].instructions.imageURL = items[i].imageURL;
          items[i].instructions.type = items[i].type;
        } else {
          items[i].instructions.verbalInstructions = items[i].instructions.repeatVariation;
        }
      }
    }
  }

  return {
    title: "Student Assessment",
    storyId: testlet.testletId,
    type: testlet.type,
    isFormative: isFormative,
    isContinuation: previousType === testlet.type,
    grade: testlet.grade,
    nonAudio: testlet.nonAudio,
    maxGradeLevel: context ? context.maxGradeLevel : false,
    levelingDelta: context ? context.levelingDelta : 0,
		referenceStoryPhrases: testlet.referenceStoryPhrases,
    // TODO: Remove this once AOS is updated to send tags
    tags: !testlet.tags || (Array.isArray(testlet.tags) && testlet.tags.length === 0) ? ['KALDI_SHARD_AMIRA_GENERAL_2'] : testlet.tags,
    chapters: [{
      phrases: phrases,
      items: items
    }]
  };
}

async function getTestlet({assignmentId, assignmentType, studentId, districtId, schoolId, grade, activityId = null, story = null}) {
  const bilingualLocale = getUserData().bilingualLocale;
  const studentLocale = getUserData().locale;
  const instructionLocale = bilingualLocale || studentLocale;
  
  // Normalize locale format from 'en-us' to 'EN_US' format
  const normalizedLocale = instructionLocale ? instructionLocale.replace('-', '_').toUpperCase() : instructionLocale;

  const variables = {
    assignmentId: assignmentId,
    studentId: studentId,
    districtId: districtId,
    schoolId: schoolId,
    gradeLevel: grade,
    locale: normalizedLocale
  };

  if (activityId) {
    variables.activityId = activityId;
  }

  const response = isMockAOSNow()
    ? await getMockTestletResponse()
    : await graphqlRequest(GET_TESTLET, variables);

  logSessionInfo("get_testlet_response", response);

  if (!response.getTestlet) {
    return null;
  }
  const testlet = response.getTestlet.testlet;
  const context = response.getTestlet.context;

  // Check for errors in the context object
  if (context && context.error) {
    const errorDetails = context.error.details;
    const errorSource = context.error.source;
    if (errorDetails || errorSource) {
      console.error(`getTestlet error`, {
        source: errorSource,
        details: errorDetails,
      });
      logSessionInfo('get_testlet_error_response', {
        source: errorSource,
        details: errorDetails,
      });
    } 
    return { error: context.error };
  }

  // If context indicates assignment is complete and there's no testlet, return shouldContinue: false
  if (context?.assignmentComplete && !testlet) {
    return { shouldContinue: false, assignmentComplete: true };
  }

  if (!testlet || !testlet.items || testlet.items.length === 0) {
    return { shouldContinue: false };
  }

  const updatedStory = buildStoryFromTestlet(testlet, story?.type, context);

  return { 
    story: updatedStory, 
    activityId: response.getTestlet.activityId, 
    testletId: testlet.testletId, 
    shouldContinue: true, 
    doNotRelevel: context?.doNotRelevel || false,
    context,
  };
}

const GET_TESTLET_BACKOFF_DELAYS = [0, 2000, 5000]; // 0, 2, 5 seconds in milliseconds

async function retryGetTestlet(params, retryCount, reason, extra = {}) {
  logSessionInfo("aos_testlet_retry", { retryCount: retryCount + 1, testletId: params.testletId, reason, ...extra });
  const delay = GET_TESTLET_BACKOFF_DELAYS[retryCount];
  if (delay > 0) await new Promise(resolve => setTimeout(resolve, delay));
  return getTestletWithRetries(params, retryCount + 1);
}

async function getTestletWithRetries(params, retryCount = 0) {
  // Include story, activityId and testletId on first try and first retry
  const requestParams = {
    ...params,
    story: retryCount < 2 ? params.story : null,
    activityId: retryCount < 2 ? params.activityId : null,
    testletId: retryCount < 2 ? params.testletId : null,
  };

  // Only the getTestlet call itself is inside try/catch so that the retry logic for null
  // and selection-error responses is not re-caught by the same catch block. If those paths
  // throw after exhausting retries the error propagates directly to the caller instead of
  // being intercepted and re-retried by a parent frame's catch, which would otherwise
  // multiply the actual call count to T(0)=15 instead of the expected 4.
  let result;
  try {
    result = await getTestlet(requestParams);
  } catch (error) {
    if (retryCount < 3) return retryGetTestlet(params, retryCount, "request_error", { error: error.message });
    throw error;
  }

  // Handle null response
  if (!result) {
    if (retryCount < 3) return retryGetTestlet(params, retryCount, "null_response");
    throw new Error("Failed to get testlet - null response after retries");
  }

  // Handle selection error
  if (result?.error?.selectionError) {
    // Check if this is a non-retryable error (permanent state errors)
    const errorDetails = result.error.details || '';
    const isNonRetryableError = errorDetails.includes('No activities to resume') ||
                                 errorDetails.includes('No activities') ||
                                 errorDetails.includes('assignment complete');

    if (isNonRetryableError) {
      // Don't retry for permanent state errors - return the error immediately
      logSessionInfo("aos_testlet_non_retryable_error", {
        error: result.error,
        details: errorDetails
      });
      return result;
    }

    // For other selection errors, retry if we haven't exceeded max retries
    if (retryCount < 3) return retryGetTestlet(params, retryCount, "selection_error", { details: errorDetails });
    throw new Error("Error getting testlet.", result.error);
  }

  console.log(`Testlet id: ${result.testletId}, activityId: ${result.activityId}, type: ${result.story ? result.story.type : "null"}`);

  // Add shouldSkipBuildPreviousAudio flag - true if this was a success on the fallback scenario (retryCount === 2)
  const shouldSkipBuildPreviousAudio = retryCount === 2;
  return {
    ...result,
    shouldSkipBuildPreviousAudio
  };
}

const RESET_ACTIVITY = `
  mutation resetActivity(
    $activityId: ID!,
    $testletId: ID!
  ) {
    resetActivity(
      activityId: $activityId,
      testletId: $testletId
    ) {
      status
    }
  }`;

/**
 * Sends a resetActivity mutation to AOS. Used to signal a testlet reset
 * when mid-session audio troubleshooting is triggered.
 */
async function resetActivity({ activityId, testletId }) {
  const variables = { activityId, testletId };
  return await graphqlRequest(RESET_ACTIVITY, variables);
}

async function onPilotAssessmentComplete(assessmentGuid, studentId) {
  const url = process.env.AUTHORIZATION_BASE_URL + "/assessment/update-assessment-status";
  const payload = {
    status: "COMPLETE",
    assessmentGuid: assessmentGuid,
    userOid: studentId,
    requestorOid: studentId
  };

  try {
    const response = await request.patch(url)
      .set('accept', '*/*')
      .set('Content-Type', 'application/json')
      .send(payload);

    return response.body;
  } catch (error) {
    console.error('Error updating assessment status:', error);
    return; // Just swallow the error, we want to let the caller move on
  }
}

async function onSpanishIsipCalibrationComplete(calibrationGuid, studentId) {
  // Reuse the existing function - both pilot and Spanish calibration use the same endpoint
  return onPilotAssessmentComplete(calibrationGuid, studentId);
}

export default {
  startAssessment: startAssessmentWithRetries,
  getTestlet: getTestletWithRetries,
  resetActivity,
  onPilotAssessmentComplete,
  onSpanishIsipCalibrationComplete,
}

async function graphqlRequest(query, variables) {
  const startTime = Date.now();
  try {
    const response = await graphQLRequestIAM(AOSurl, query, variables);
    const duration = Date.now() - startTime;

    if (response.errors) {
      console.log(response.errors);
      networkLogger.logGraphQLError(
        'AssessmentOrchestrationService',
        AOSurl,
        null,
        response.errors,
        duration
      );
      const graphqlError = new Error(response.errors[0].message);
      graphqlError.isGraphQLError = true;
      throw graphqlError;
    }

    return response.data;
  } catch (error) {
    if (!error.isGraphQLError) {
      networkLogger.logGraphQLError(
        'AssessmentOrchestrationService',
        AOSurl,
        null,
        error,
        Date.now() - startTime
      );
    }
    throw error;
  }
}